import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy01, Play, RefreshCw01 } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useConnectors } from "@/lib/connectors";
import { fetchTokenPassword, invalidPassword } from "@/lib/apiTools";
import {
  executeEchannelsTool,
  getEchannelsToken,
  useEchannelsHealth,
  useEchannelsMeta,
  type CachedTransaction,
  type EchannelsExecuteResponse,
  type EchannelsMeta,
} from "@/lib/echannelsTools";
import { ECH_GROUPS, buildEchannelsBody, type EchFieldSpec, type EchGroup, type EchOperationSpec } from "./echannelsTools/catalog";
import { Field, inputCls } from "./shared";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppSelect } from "@/components/app/AppSelect";
import { TokenPicker } from "@/components/app/TokenPicker";
import { Table } from "@/components/application/table/table";

const TYPE_MAP: Record<number, string> = {
  1: "Login",
  2: "Payment",
  3: "Domestic",
  4: "SEPA",
  5: "Foreign",
  6: "eIDP Login",
  7: "Sign Doc",
};
const STATUS_MAP: Record<number, string> = {
  1: "Pending",
  2: "Confirmed",
  3: "Rejected",
  4: "Expired",
  5: "Cancelled",
  6: "Failed",
  7: "Processing",
  8: "Expired",
};

interface Cache {
  transactions: CachedTransaction[];
  tokenId: string;
}

interface AuthCtx {
  subject: string;
  accessTokenOverride: string;
}

/**
 * eChannels Tools — CSOB eChannels konzole. OAuth2 token panel + 21 operací
 * v 7 skupinách + cache workflow pending transakcí. Port legacy
 * admin/echannels-tools.html + echannels-tools.js. Gated na CSOB_ECHANNELS.
 */
export function EChannelsToolsPage() {
  const { t } = useTranslation();
  const { data: connectors, isLoading } = useConnectors();
  const hasConnector = (connectors ?? []).some((c) => c.connectorType === "CSOB_ECHANNELS");
  const meta = useEchannelsMeta(hasConnector);
  const health = useEchannelsHealth(hasConnector);

  // OAuth2 kontext (platí pro všechny operace)
  const [subject, setSubject] = useState("60322504");
  const [accessTokenOverride, setAccessTokenOverride] = useState("");
  const auth: AuthCtx = { subject, accessTokenOverride };

  // Cache pending transakcí (getAuthList → confirm/reject/detail)
  const [cache, setCache] = useState<Cache>({ transactions: [], tokenId: "" });
  const onCacheUpdate = (transactions: CachedTransaction[], tokenId?: string) =>
    setCache((c) => ({ transactions, tokenId: tokenId || c.tokenId }));

  const [groupKey, setGroupKey] = useState<EchGroup>("activation");
  const group = ECH_GROUPS.find((g) => g.group === groupKey) ?? ECH_GROUPS[0];
  const [opId, setOpId] = useState(group.operations[0].id);
  const op = group.operations.find((o) => o.id === opId) ?? group.operations[0];

  const selectGroup = (gk: EchGroup) => {
    const g = ECH_GROUPS.find((x) => x.group === gk)!;
    setGroupKey(gk);
    setOpId(g.operations[0].id);
  };

  if (isLoading) return <LoadingIndicator />;

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("echtools.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("echtools.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      {!hasConnector ? (
        <div className="flex items-center justify-center rounded-xl bg-primary p-8 text-sm text-tertiary ring-1 ring-secondary">
          {t("echtools.noConnector")}
        </div>
      ) : (
        <>
          {/* Status + health */}
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-primary p-4 ring-1 ring-secondary">
            <span className="text-sm text-tertiary">{t("echtools.baseUrl")}:</span>
            <span className="min-w-0 flex-1 truncate font-mono text-sm text-secondary">{meta.data?.baseUrl || "—"}</span>
            <Badge type="pill-color" size="sm" color={health.isFetching ? "gray" : health.data?.healthy ? "success" : "error"}>
              {health.isFetching
                ? t("soaptools.health.checking")
                : health.data?.healthy
                  ? t("soaptools.health.healthy")
                  : t("soaptools.health.unhealthy")}
            </Badge>
            <Button size="sm" color="secondary" iconLeading={RefreshCw01} isDisabled={health.isFetching} onPress={() => void health.refetch()}>
              {t("soaptools.health.refresh")}
            </Button>
          </div>

          {/* OAuth2 panel */}
          <OAuth2Panel
            meta={meta.data}
            subject={subject}
            onSubject={setSubject}
            override={accessTokenOverride}
            onOverride={setAccessTokenOverride}
          />

          {/* Group tabs */}
          <div className="flex flex-wrap gap-1">
            {ECH_GROUPS.map((g) => (
              <button
                key={g.group}
                type="button"
                onClick={() => selectGroup(g.group)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
                  groupKey === g.group ? "bg-secondary text-primary ring-1 ring-secondary" : "text-secondary hover:bg-primary_hover"
                }`}
              >
                {t(g.label)}
              </button>
            ))}
          </div>

          {/* Operation sub-tabs */}
          <div className="flex flex-wrap gap-1 border-t border-secondary pt-2">
            {group.operations.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setOpId(o.id)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  opId === o.id ? "bg-brand-solid text-white" : "text-secondary hover:bg-primary_hover"
                }`}
              >
                {t(o.label)}
              </button>
            ))}
          </div>

          <OperationForm
            key={op.id}
            op={op}
            auth={auth}
            cache={cache}
            onCacheUpdate={onCacheUpdate}
          />

          <TransactionCachePanel cache={cache} auth={auth} onCacheUpdate={onCacheUpdate} />
        </>
      )}
    </div>
  );
}

function OAuth2Panel({
  meta,
  subject,
  onSubject,
  override,
  onOverride,
}: {
  meta?: EchannelsMeta;
  subject: string;
  onSubject: (v: string) => void;
  override: string;
  onOverride: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [scopeType, setScopeType] = useState<"activation" | "signing">("activation");
  const resolvedScope = (scopeType === "signing" ? meta?.signingScope : meta?.scope) ?? "";
  const [token, setToken] = useState<{ access_token: string; token_type: string; expires_in: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getToken = async () => {
    setError(null);
    setBusy(true);
    try {
      const r = await getEchannelsToken(subject, resolvedScope);
      if (r.success && r.token) setToken(r.token);
      else setError(r.error ?? "Token exchange failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="text-md font-semibold text-primary">{t("echtools.oauth.title")}</h2>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label={`${t("echtools.oauth.subject")} *`} hint={t("echtools.oauth.subject.hint")}>
          <input className={inputCls} value={subject} onChange={(e) => onSubject(e.target.value)} placeholder="60322504" />
        </Field>
        <Field label={t("echtools.oauth.clientId")}>
          <input className={inputCls} value={meta?.clientId ?? ""} readOnly />
        </Field>
        <Field label={t("echtools.oauth.scope")} hint={t("echtools.oauth.scope.hint")}>
          <div className="flex flex-col gap-2">
            <div className="flex gap-4">
              {(["activation", "signing"] as const).map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm text-primary">
                  <input
                    type="radio"
                    checked={scopeType === s}
                    disabled={s === "signing" && !meta?.signingOauth2Enabled}
                    onChange={() => {
                      setScopeType(s);
                      setToken(null);
                    }}
                  />
                  {t(`echtools.oauth.scope.${s}`)}
                </label>
              ))}
            </div>
            <input className={inputCls} value={resolvedScope} readOnly />
          </div>
        </Field>
        <Field label={t("echtools.oauth.override")} hint={t("echtools.oauth.override.hint")}>
          <input className={inputCls} value={override} onChange={(e) => onOverride(e.target.value)} placeholder="Bearer …" />
        </Field>
      </div>

      <div className="flex items-center gap-3">
        <Button size="md" color="secondary" isDisabled={busy || !subject.trim()} onPress={() => void getToken()}>
          {busy ? t("testing.running") : t("echtools.oauth.getToken")}
        </Button>
        {token && (
          <>
            <span className="text-xs text-tertiary">
              {t("echtools.oauth.expiresIn")}: {token.expires_in}s · {token.token_type}
            </span>
            <Button
              size="sm"
              color="tertiary"
              iconLeading={Copy01}
              onPress={() => void navigator.clipboard?.writeText(token.access_token)}
            >
              {t("common.copy")}
            </Button>
          </>
        )}
      </div>
      {error && <p className="text-sm text-error-primary">{error}</p>}
      {token && (
        <pre className="max-h-32 overflow-auto rounded-md bg-secondary p-2 font-mono text-xs break-all text-secondary ring-1 ring-secondary">
          {token.access_token}
        </pre>
      )}
    </div>
  );
}

function OperationForm({
  op,
  auth,
  cache,
  onCacheUpdate,
}: {
  op: EchOperationSpec;
  auth: AuthCtx;
  cache: Cache;
  onCacheUpdate: (transactions: CachedTransaction[], tokenId?: string) => void;
}) {
  const { t } = useTranslation();

  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const v: Record<string, unknown> = {};
    for (const f of op.fields) if (f.default !== undefined) v[f.name] = f.default;
    return v;
  });
  const setVal = (name: string, value: unknown) =>
    setValues((s) => {
      const next = { ...s, [name]: value };
      // rejectAuthRequest: guid z cache auto-vyplní transactionId/signId/usedDeviceAuthType.
      if (op.autofillFromCache) {
        const f = op.fields.find((x) => x.name === name);
        if (f?.kind === "cacheGuid") {
          const tx = cache.transactions.find((x) => x.guid === value);
          if (tx) {
            if (tx.transactionId != null) next.transactionId = tx.transactionId;
            if (tx.signId != null) next.signId = String(tx.signId);
            if (tx.deviceAuthorizationType != null) next.usedDeviceAuthType = String(tx.deviceAuthorizationType);
          }
        }
      }
      return next;
    });

  const [result, setResult] = useState<EchannelsExecuteResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const run = async () => {
    setValidationError(null);
    for (const f of op.fields) {
      if (f.required && !String(values[f.name] ?? "").trim()) {
        setValidationError(t("apitools.required", { field: t(f.label) }));
        return;
      }
    }
    if (op.needsCacheToken && !cache.tokenId) {
      setValidationError(t("echtools.err.noCacheToken"));
      return;
    }

    const body = buildEchannelsBody(op, values);
    body.subject = auth.subject;
    if (auth.accessTokenOverride.trim()) body.accessTokenOverride = auth.accessTokenOverride.trim();
    if (op.needsCacheToken) body.tokenId = cache.tokenId;

    setRunning(true);
    setResult(null);
    try {
      const res = await executeEchannelsTool(op.execAs ?? op.id, body);
      setResult(res);
      const cached = res.envelope.result?.cachedTransactions;
      if (cached) onCacheUpdate(cached, res.envelope.result?.selectedTokenId);
    } catch (e) {
      setResult({ envelope: { success: false, error: (e as Error).message }, securityBlock: false, rawStatus: 0 });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <div>
          <h2 className="text-md font-semibold text-primary">{t(op.label)}</h2>
          {op.subtitle && <p className="font-mono text-xs text-tertiary">{op.subtitle}</p>}
        </div>

        {op.fields.map((f) => (
          <EchFieldInput
            key={f.name}
            field={f}
            value={values[f.name]}
            onChange={(v) => setVal(f.name, v)}
            cache={cache}
            siblingTokenId={String(values.tokenId ?? "")}
          />
        ))}

        {op.needsCacheToken && (
          <p className="text-xs text-tertiary">
            {t("echtools.cacheToken")}: <span className="font-mono">{cache.tokenId || "—"}</span>
          </p>
        )}

        {validationError && <p className="text-sm text-error-primary">{validationError}</p>}

        <div>
          <Button size="lg" iconLeading={Play} isDisabled={running} onPress={() => void run()}>
            {running ? t("testing.running") : t("echtools.execute")}
          </Button>
        </div>
      </div>

      <EchEnvelopeViewer result={result} />
    </div>
  );
}

function EchFieldInput({
  field,
  value,
  onChange,
  cache,
  siblingTokenId,
}: {
  field: EchFieldSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  cache: Cache;
  siblingTokenId: string;
}) {
  const { t } = useTranslation();
  const label = `${t(field.label)}${field.required ? " *" : ""}`;

  switch (field.kind) {
    case "textarea":
      return (
        <Field label={label}>
          <textarea className={`${inputCls} h-24`} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
    case "number":
      return (
        <Field label={label}>
          <input className={inputCls} type="number" value={String(value ?? "")} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
    case "select":
      return (
        <Field label={label}>
          <AppSelect value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label.includes(".") ? t(o.label) : o.label}
              </option>
            ))}
          </AppSelect>
        </Field>
      );
    case "uuid":
      return (
        <Field label={label}>
          <div className="flex gap-2">
            <input className={inputCls} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} />
            <Button size="md" color="secondary" onPress={() => onChange(crypto.randomUUID())}>
              {t("echtools.genUuid")}
            </Button>
          </div>
        </Field>
      );
    case "tokenSelect":
      return (
        <Field label={label}>
          <TokenPicker
            value={String(value ?? "")}
            onChange={onChange}
            authServerType="CSOB_ECHANNELS"
            activeOnly={field.activeOnly}
            placeholder={t("apitools.selectToken")}
          />
        </Field>
      );
    case "cacheGuid":
      return (
        <Field label={label}>
          <AppSelect value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            <option value="">{t("echtools.selectTransaction")}</option>
            {cache.transactions.map((tx) => (
              <option key={tx.guid} value={tx.guid}>
                {tx.guid.substring(0, 8)}… ({tx.requestTypeId != null ? TYPE_MAP[tx.requestTypeId] ?? tx.requestTypeId : "?"})
              </option>
            ))}
          </AppSelect>
        </Field>
      );
    case "pinRadio":
      return (
        <Field label={label}>
          <div className="flex gap-4">
            {(["valid", "invalid"] as const).map((pt) => (
              <label key={pt} className="flex items-center gap-2 text-sm text-primary">
                <input type="radio" checked={String(value ?? "valid") === pt} onChange={() => onChange(pt)} />
                {t(pt === "valid" ? "apitools.validPin" : "apitools.invalidPin")}
              </label>
            ))}
          </div>
        </Field>
      );
    case "password":
      return <EchPasswordField label={label} value={String(value ?? "")} onChange={onChange} tokenId={siblingTokenId} />;
    default:
      return (
        <Field label={label}>
          <input className={inputCls} value={String(value ?? "")} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
  }
}

/**
 * Heslo pole s valid/invalid fill z hesla vybraného tokenu (`tokenId` téže
 * operace, verifyOtp). Bez tokenId jsou tlačítka disabled.
 */
function EchPasswordField({
  label,
  value,
  onChange,
  tokenId,
}: {
  label: string;
  value: string;
  onChange: (v: unknown) => void;
  tokenId: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const fill = async (valid: boolean) => {
    if (!tokenId) return;
    setBusy(true);
    try {
      const real = await fetchTokenPassword(tokenId);
      onChange(valid ? real : invalidPassword(real));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
        <Button size="md" color="secondary" isDisabled={!tokenId || busy} onPress={() => void fill(true)}>
          {t("apitools.validPin")}
        </Button>
        <Button size="md" color="secondary" isDisabled={!tokenId || busy} onPress={() => void fill(false)}>
          {t("apitools.invalidPin")}
        </Button>
      </div>
    </Field>
  );
}

function EchEnvelopeViewer({ result }: { result: EchannelsExecuteResponse | null }) {
  const { t } = useTranslation();
  if (!result) {
    return (
      <div className="flex items-center justify-center rounded-xl bg-primary p-5 text-sm text-tertiary ring-1 ring-secondary">
        {t("soaptools.executeHint")}
      </div>
    );
  }

  const { envelope, securityBlock } = result;
  if (securityBlock) {
    return (
      <div className="flex flex-col gap-2 rounded-xl bg-primary p-5 ring-1 ring-error">
        <h2 className="text-md font-semibold text-error-primary">{t("apitools.sessionStale.title")}</h2>
        <p className="text-sm text-secondary">{t("apitools.sessionStale.body")}</p>
      </div>
    );
  }

  const status = envelope.httpStatus;
  const ti = envelope.tokenInfo;
  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex flex-wrap items-center gap-2">
        <Badge type="pill-color" size="sm" color={envelope.success ? "success" : "error"}>
          {envelope.success ? t("apitools.success") : t("apitools.error")}
        </Badge>
        {status != null && (
          <Badge type="pill-color" size="sm" color={status >= 200 && status < 300 ? "success" : "error"}>
            HTTP {status}
          </Badge>
        )}
        {ti?.source && <span className="text-xs text-tertiary">token: {ti.source}</span>}
      </div>

      {envelope.result?.generatedSignature && (
        <div className="text-xs">
          <span className="font-semibold text-tertiary">{t("echtools.signature")}:</span>{" "}
          <span className="font-mono break-all text-secondary">{envelope.result.generatedSignature}</span>
          {envelope.result.usedValidPin != null && (
            <Badge type="pill-color" size="sm" color={envelope.result.usedValidPin ? "success" : "warning"} className="ml-2">
              {envelope.result.usedValidPin ? t("apitools.validPin") : t("apitools.invalidPin")}
            </Badge>
          )}
        </div>
      )}

      {envelope.success ? (
        <div>
          <h4 className="mb-1 text-xs font-semibold text-tertiary">{t("soaptools.response")}</h4>
          <pre className="max-h-96 overflow-auto rounded-md bg-secondary p-3 font-mono text-xs text-secondary ring-1 ring-secondary">
            {JSON.stringify(envelope.result?.response ?? envelope.result, null, 2)}
          </pre>
        </div>
      ) : (
        <div className="rounded-lg bg-secondary p-3 text-sm ring-1 ring-error">
          <span className="text-error-primary">{envelope.error || t("common.loadError")}</span>
        </div>
      )}

      {envelope.testTraceId && <p className="text-xs text-tertiary">traceId: {envelope.testTraceId}</p>}
    </div>
  );
}

function TransactionCachePanel({
  cache,
  auth,
  onCacheUpdate,
}: {
  cache: Cache;
  auth: AuthCtx;
  onCacheUpdate: (transactions: CachedTransaction[], tokenId?: string) => void;
}) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [busyGuid, setBusyGuid] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return cache.transactions
      .filter((tx) => !statusFilter || String(tx.requestStatusId ?? "") === statusFilter)
      .filter((tx) => !typeFilter || String(tx.requestTypeId ?? "") === typeFilter)
      .slice()
      .sort((a, b) => (b.expirationDate ?? "").localeCompare(a.expirationDate ?? ""));
  }, [cache.transactions, statusFilter, typeFilter]);

  if (cache.transactions.length === 0) return null;

  const authBody = (): Record<string, unknown> => {
    const b: Record<string, unknown> = { subject: auth.subject };
    if (auth.accessTokenOverride.trim()) b.accessTokenOverride = auth.accessTokenOverride.trim();
    return b;
  };

  const call = async (operation: string, extra: Record<string, unknown>, guid: string) => {
    setBusyGuid(guid);
    try {
      const res = await executeEchannelsTool(operation, { ...authBody(), ...extra });
      const cached = res.envelope.result?.cachedTransactions;
      if (cached) onCacheUpdate(cached);
    } finally {
      setBusyGuid(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-md font-semibold text-primary">{t("echtools.cache.title")}</h2>
        <div className="flex gap-2">
          <AppSelect fullWidth={false} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t("echtools.cache.allStatuses")}</option>
            {Object.entries(STATUS_MAP).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </AppSelect>
          <AppSelect fullWidth={false} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">{t("echtools.cache.allTypes")}</option>
            {Object.entries(TYPE_MAP).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </AppSelect>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg ring-1 ring-secondary">
        <Table aria-label={t("echtools.cache.title")} selectionMode="none" size="sm">
          <Table.Header>
            <Table.Head isRowHeader id="guid" label="GUID" />
            <Table.Head id="type" label={t("echtools.cache.type")} />
            <Table.Head id="status" label={t("echtools.cache.status")} />
            <Table.Head id="ippid" label="IPPID" />
            <Table.Head id="expiration" label={t("echtools.cache.expiration")} />
            <Table.Head id="detail" label={t("echtools.cache.detail")} />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <Table.Body items={filtered}>
            {(tx) => {
              const busy = busyGuid === tx.guid;
              return (
                <Table.Row id={tx.guid}>
                  <Table.Cell className="font-mono text-secondary">{tx.guid.substring(0, 8)}…</Table.Cell>
                  <Table.Cell className="text-secondary">{tx.requestTypeId != null ? TYPE_MAP[tx.requestTypeId] ?? tx.requestTypeId : "—"}</Table.Cell>
                  <Table.Cell className="text-secondary">{tx.requestStatusId != null ? STATUS_MAP[tx.requestStatusId] ?? tx.requestStatusId : "—"}</Table.Cell>
                  <Table.Cell className="text-tertiary">{tx.ippid ?? "—"}</Table.Cell>
                  <Table.Cell className="text-tertiary">{tx.expirationDate ?? "—"}</Table.Cell>
                  <Table.Cell>
                    <Badge type="pill-color" size="sm" color={tx.detailLoaded ? "success" : "gray"}>
                      {tx.detailLoaded ? t("echtools.cache.loaded") : t("echtools.cache.notLoaded")}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-wrap justify-end gap-1">
                    <Button size="sm" color="tertiary" isDisabled={busy} onPress={() => void call("fetchTransactionDetail", { guid: tx.guid }, tx.guid)}>
                      {t("echtools.cache.fetchDetail")}
                    </Button>
                    <Button
                      size="sm"
                      color="tertiary"
                      isDisabled={busy || !tx.detailLoaded || !cache.tokenId}
                      onPress={() => void call("confirmTransactionWithOtp", { guid: tx.guid, tokenId: cache.tokenId, useValidPin: "true" }, tx.guid)}
                    >
                      {t("echtools.cache.confirmValid")}
                    </Button>
                    <Button
                      size="sm"
                      color="tertiary"
                      isDisabled={busy || !tx.detailLoaded || !cache.tokenId}
                      onPress={() => void call("confirmTransactionWithOtp", { guid: tx.guid, tokenId: cache.tokenId, useValidPin: "false" }, tx.guid)}
                    >
                      {t("echtools.cache.confirmInvalid")}
                    </Button>
                    <Button
                      size="sm"
                      color="tertiary-destructive"
                      isDisabled={busy || !tx.detailLoaded}
                      onPress={() => void call("rejectTransactionFromCache", { guid: tx.guid }, tx.guid)}
                    >
                      {t("echtools.cache.reject")}
                    </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            }}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
}
